import { isEmpty, zipWith } from 'lodash'
import { createPublicClient, http } from 'viem'
import { mainnet } from 'viem/chains'
import { normalize } from 'viem/ens'

import { getChain } from '~/constants/chains'
import { IAccount } from '~/types'
import { EvmAddress } from '~/utils/address'
import { getReadReverseRecordsContract } from '~/utils/reverseRecords'

export const ensPublicClient = createPublicClient({
  chain: mainnet,
  transport: http(
    `https://eth-mainnet.g.alchemy.com/v2/${process.env.ALCHEMY_API_KEY}`,
  ),
})
const reverseRecords = getReadReverseRecordsContract({
  publicClient: ensPublicClient,
})

export const reverseENSLookup = async (
  accounts: IAccount[],
): Promise<(string | undefined)[] | undefined> => {
  if (!reverseRecords) return

  // Step 1: mainnet reverse resolution
  const ensNames = (await reverseRecords.read.getNames([
    accounts.map((a) => EvmAddress.format(a.address)),
  ])) as string[]

  const ensMappings = zipWith(accounts, ensNames, (account, ens) => ({
    account,
    ens,
  }))

  return await Promise.all(
    ensMappings.map(async ({ account, ens }) => {
      const { address, chainId } = account
      try {
        if (isEmpty(ens) || normalize(ens) !== ens) {
          return
        }
      } catch {
        return
      }

      // Step 2: chain forward resolution
      const chain = getChain(chainId)
      if (chain?.supportsEns && chain.ensCoinType !== undefined) {
        const coinType = BigInt(chain.ensCoinType)
        const chainForwardAddress = await ensPublicClient.getEnsAddress({
          name: normalize(ens),
          coinType: BigInt(coinType),
        })

        if (chainForwardAddress) {
          if (chainForwardAddress.toLowerCase() === address.toLowerCase()) {
            return ens
          } else {
            return
          }
        }
      }

      // Step 3a: mainnet forward resolution
      const mainnetForwardAddress = await ensPublicClient.getEnsAddress({
        name: normalize(ens),
      })
      if (
        !mainnetForwardAddress ||
        mainnetForwardAddress.toLowerCase() !== address.toLowerCase()
      ) {
        return
      }

      // Step 3b: mainnet smart contract check
      const bytecode = await ensPublicClient.getBytecode({
        address: EvmAddress.format(address),
      })
      if (bytecode) {
        return
      }

      return ens
    }),
  )
}
